import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import DsButton from '../DsButton';
import DsModal from '../DsModal';
import WorkAvailabilityHelp from './WorkAvailabilityHelp';
import { useWorkAvailabilityPrompt } from './useWorkAvailabilityPrompt';
import {
  createAdminWorkAvailabilityEntryBatch,
  dismissAdminWorkAvailabilityPrompt,
  updateAdminWorkAvailabilitySettings,
} from '../../../api/workAvailability';
import WorkAvailabilityForm, {
  createDefaultAvailabilityDraft,
  draftToEntryPayloads,
} from './WorkAvailabilityForm';
import '../../../css/common/WorkAvailability.css';

type ModalProps = {
  open: boolean;
  onClose: () => void | Promise<void>;
  title: string;
  ariaLabel: string;
  size?: string;
  dismissible?: boolean;
  closeOnBack?: boolean;
  footer?: React.ReactNode;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
};
type ButtonProps = {
  variant?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick: () => void | Promise<void>;
  children: React.ReactNode;
};

const Modal = DsModal as React.ComponentType<ModalProps>;
const Button = DsButton as React.ComponentType<ButtonProps>;

const errorMessage = (error: unknown, fallback: string) => (
  error instanceof Error ? error.message : fallback
);

// Mounted inside DsModal's portal, not when the reservation response arrives.
function PromptPresented({ onPresented }: { onPresented: () => void }) {
  useEffect(() => { onPresented(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function AdminWorkAvailabilityPrompt() {
  const { user, checking } = useAuth();
  const navigate = useNavigate();
  const { claim, setClaim, close: closePrompt, presented } = useWorkAvailabilityPrompt(
    checking ? null : user?.userId ?? user?.id ?? null,
  );
  const [draft, setDraft] = useState(createDefaultAvailabilityDraft());
  const [saving, setSaving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState('');
  const busy = saving || skipping;

  useEffect(() => {
    if (claim) setDraft(createDefaultAvailabilityDraft(claim.settings));
    // Only a new reservation initializes input; background updates preserve drafts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claim?.reservationToken]);

  const close = () => {
    if (!busy) closePrompt();
  };

  const skipToday = async () => {
    if (!claim || busy) return;
    setSkipping(true);
    setError('');
    try {
      await dismissAdminWorkAvailabilityPrompt(false);
      closePrompt();
    } catch (skipError) {
      setError(errorMessage(skipError, '今日は表示しない設定を保存できませんでした。もう一度お試しください。'));
    } finally {
      setSkipping(false);
    }
  };

  const openSettings = () => {
    if (busy) return;
    closePrompt();
    navigate('/admin/settings/work-availability');
  };

  const save = async () => {
    if (!claim || busy) return;
    setSaving(true);
    setError('');
    try {
      let settings = claim.settings;
      if (claim.promptType === 'setup' || !settings.enabled) {
        const updated = await updateAdminWorkAvailabilitySettings({
          enabled: true,
          timezone: settings.timezone || 'Asia/Tokyo',
          dailyPromptMode: 'when_unplanned',
          defaultStartLocal: settings.defaultStartLocal || null,
          defaultEndLocal: settings.defaultEndLocal || null,
          shareNextStartWithArtists: false,
          expectedRevision: settings.revision || null,
        });
        settings = updated.settings;
        setClaim(current => current ? { ...current, settings } : current);
      }
      await createAdminWorkAvailabilityEntryBatch({
        entries: draftToEntryPayloads(draft, settings.timezone, 'daily_prompt'),
      });
      closePrompt();
      window.dispatchEvent(new CustomEvent('casticart:work-availability-updated'));
    } catch (saveError) {
      setError(errorMessage(saveError, '勤務予定を登録できませんでした。'));
    } finally {
      setSaving(false);
    }
  };

  // 自動確認はページ履歴を増やさない。閉じる後始末で専用ページへの遷移を巻き戻さない。
  return (
    <Modal
      open={Boolean(claim)}
      onClose={close}
      title="今日・明日の予定を設定"
      ariaLabel="今日・明日の予定を設定"
      headerActions={<WorkAvailabilityHelp topic="overview" />}
      size="md"
      dismissible={!busy}
      closeOnBack={false}
      footer={(
        <>
          <Button variant="secondary" loading={skipping} disabled={saving} onClick={skipToday}>今日は設定しない</Button>
          <Button variant="primary" loading={saving} disabled={skipping} onClick={save}>この予定を保存</Button>
          <Button variant="secondary" disabled={busy} onClick={openSettings}>詳細設定</Button>
        </>
      )}
    >
      {claim ? <PromptPresented key={claim.reservationToken} onPresented={presented} /> : null}
      {error ? <p className="work-availability__error" role="alert">{error}</p> : null}
      <WorkAvailabilityForm draft={draft} onChange={setDraft} disabled={busy} />
    </Modal>
  );
}
